import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { brl, maskCEP, maskCPF, maskPhone } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const steps = ["Identificação", "Entrega", "Pagamento"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: user?.phone || "",
    customer_cpf: "",
    cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "",
    shipping_method: "", shipping_cost: 0,
    payment_method: "pix", installments: 1,
  });

  useEffect(() => {
    if (items.length === 0 && !finished && !loading) nav("/carrinho");
  }, [items.length, nav, finished, loading]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: user.name, customer_email: user.email, customer_phone: user.phone || "" }));
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupCep = async (cep) => {
    update("cep", maskCEP(cep));
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    try {
      const { data } = await api.get(`/cep/${clean}`);
      setForm((f) => ({ ...f, street: data.logradouro || f.street, neighborhood: data.bairro || f.neighborhood, city: data.localidade || f.city, state: data.uf || f.state }));
      // Fetch shipping options
      const sh = await api.post("/shipping/calculate", { cep: clean, subtotal });
      setShippingOptions(sh.data.options);
    } catch { toast.error("CEP não encontrado"); }
  };

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setCouponData(data);
      toast.success(`Cupom ${data.code} aplicado!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cupom inválido");
    }
  };

  const discount = couponData ? (
    couponData.discount_type === "percent" ? subtotal * (couponData.discount_value/100) :
    couponData.discount_type === "fixed" ? couponData.discount_value : 0
  ) : 0;
  const pixDisc = form.payment_method === "pix" ? subtotal * 0.05 : 0;
  const freeShipByCoupon = couponData?.discount_type === "free_shipping";
  const finalShipping = freeShipByCoupon ? 0 : form.shipping_cost;
  const total = Math.max(0, subtotal - discount - pixDisc + finalShipping);

  const next = () => {
    if (step === 0) {
      if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.customer_cpf) {
        return toast.error("Preencha todos os campos de identificação");
      }
    }
    if (step === 1) {
      if (!form.cep || !form.street || !form.number || !form.city || !form.state) return toast.error("Preencha o endereço");
      if (!form.shipping_method) return toast.error("Selecione o frete");
    }
    setStep(step + 1);
  };

  const finish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        customer_cpf: form.customer_cpf,
        address: {
          cep: form.cep, street: form.street, number: form.number, complement: form.complement,
          neighborhood: form.neighborhood, city: form.city, state: form.state, recipient: form.customer_name,
        },
        items: items.map((it) => ({ product_id: it.product.id, variant: it.variant, quantity: it.quantity })),
        payment_method: form.payment_method,
        shipping_method: form.shipping_method,
        shipping_cost: finalShipping,
        coupon_code: couponData?.code || null,
        installments: form.installments,
      });
      setFinished(true);
      nav(`/pedido/${data.id}/confirmacao`, { replace: true });
      setTimeout(() => clear(), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao finalizar pedido");
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="font-serif text-3xl text-[#4A3B40] mb-6">Finalizar pedido</h1>
      <div className="flex items-center gap-2 mb-6">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 text-sm ${i<=step?'text-[#C9708B]':'text-[#8C7A80]'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${i<=step?'bg-[#C9708B] text-white':'bg-[#F9E1E8] text-[#8C7A80]'}`}>{i+1}</div>
              <span className="hidden sm:inline">{s}</span>
            </div>
            {i < steps.length-1 && <div className={`h-0.5 flex-1 ${i<step?'bg-[#C9708B]':'bg-[#F9E1E8]'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#F9E1E8] p-6 space-y-4">
          {step === 0 && (
            <div className="space-y-3" data-testid="checkout-step-identification">
              <h2 className="font-serif text-xl text-[#4A3B40]">Identificação</h2>
              <input className="input" placeholder="Nome completo" value={form.customer_name} onChange={(e)=>update("customer_name", e.target.value)} data-testid="checkout-name" />
              <input className="input" placeholder="E-mail" type="email" value={form.customer_email} onChange={(e)=>update("customer_email", e.target.value)} data-testid="checkout-email" />
              <div className="grid grid-cols-2 gap-3">
                <input className="input" placeholder="(00) 00000-0000" value={form.customer_phone} onChange={(e)=>update("customer_phone", maskPhone(e.target.value))} data-testid="checkout-phone" />
                <input className="input" placeholder="CPF" value={form.customer_cpf} onChange={(e)=>update("customer_cpf", maskCPF(e.target.value))} data-testid="checkout-cpf" />
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3" data-testid="checkout-step-shipping">
              <h2 className="font-serif text-xl text-[#4A3B40]">Entrega</h2>
              <input className="input" placeholder="CEP (ex: 01310-100)" value={form.cep} onChange={(e)=>lookupCep(e.target.value)} data-testid="checkout-cep" />
              <div className="grid grid-cols-3 gap-3">
                <input className="input col-span-2" placeholder="Rua" value={form.street} onChange={(e)=>update("street", e.target.value)} />
                <input className="input" placeholder="Número" value={form.number} onChange={(e)=>update("number", e.target.value)} />
              </div>
              <input className="input" placeholder="Complemento (opcional)" value={form.complement} onChange={(e)=>update("complement", e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <input className="input" placeholder="Bairro" value={form.neighborhood} onChange={(e)=>update("neighborhood", e.target.value)} />
                <input className="input" placeholder="Cidade" value={form.city} onChange={(e)=>update("city", e.target.value)} />
              </div>
              <input className="input w-24" placeholder="UF" value={form.state} onChange={(e)=>update("state", e.target.value.toUpperCase().slice(0,2))} />
              {shippingOptions.length > 0 && (
                <div className="pt-4 border-t border-[#F9E1E8] space-y-2">
                  <p className="font-serif text-[#4A3B40] font-semibold">Opções de frete</p>
                  {shippingOptions.map((o) => (
                    <label key={o.code} className={`flex items-center justify-between p-3 border-2 rounded-xl cursor-pointer ${form.shipping_method === o.code ? 'border-[#C9708B] bg-[#F9E1E8]/30' : 'border-[#F9E1E8]'}`} data-testid={`ship-option-${o.code}`}>
                      <div className="flex items-center gap-3">
                        <input type="radio" name="ship" checked={form.shipping_method === o.code} onChange={() => setForm({...form, shipping_method: o.code, shipping_cost: o.price})} className="accent-[#C9708B]" />
                        <div>
                          <p className="font-semibold text-[#4A3B40] text-sm">{o.name}</p>
                          <p className="text-xs text-[#8C7A80]">{o.days}</p>
                        </div>
                      </div>
                      <span className={`font-bold ${o.free?'text-[#7DBE9E]':'text-[#C9708B]'}`}>{o.free?'Grátis':brl(o.price)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3" data-testid="checkout-step-payment">
              <h2 className="font-serif text-xl text-[#4A3B40]">Pagamento</h2>
              {[
                {code:"pix", name:"PIX (5% OFF)", desc:"Aprovação em instantes"},
                {code:"credit_card", name:"Cartão de crédito", desc:"Em até 6x sem juros"},
                {code:"boleto", name:"Boleto bancário", desc:"Vencimento em 2 dias"},
              ].map((p) => (
                <label key={p.code} className={`flex items-center justify-between p-3 border-2 rounded-xl cursor-pointer ${form.payment_method===p.code?'border-[#C9708B] bg-[#F9E1E8]/30':'border-[#F9E1E8]'}`} data-testid={`pay-${p.code}`}>
                  <div className="flex items-center gap-3">
                    <input type="radio" name="pay" checked={form.payment_method===p.code} onChange={() => update("payment_method", p.code)} className="accent-[#C9708B]" />
                    <div>
                      <p className="font-semibold text-[#4A3B40] text-sm">{p.name}</p>
                      <p className="text-xs text-[#8C7A80]">{p.desc}</p>
                    </div>
                  </div>
                </label>
              ))}
              {form.payment_method === "credit_card" && (
                <div className="pt-3 space-y-2">
                  <input className="input" placeholder="Número do cartão (DEMO)" />
                  <div className="grid grid-cols-3 gap-2">
                    <input className="input" placeholder="MM/AA" />
                    <input className="input" placeholder="CVV" />
                    <select className="input" value={form.installments} onChange={(e)=>update("installments", Number(e.target.value))}>
                      {[1,2,3,4,5,6].map(i => <option key={i} value={i}>{i}x de {brl(total/i)}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <p className="text-xs text-[#8C7A80] mt-2">💡 Modo DEMO: nenhuma cobrança real será feita.</p>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            {step > 0 && <button onClick={()=>setStep(step-1)} className="btn-lara-outline">Voltar</button>}
            {step < 2 ? (
              <button onClick={next} className="btn-lara ml-auto" data-testid="checkout-next">Continuar</button>
            ) : (
              <button onClick={finish} disabled={loading} className="btn-lara ml-auto" data-testid="checkout-finish">
                {loading?"Processando...":"Confirmar pedido"}
              </button>
            )}
          </div>
        </div>

        <aside className="bg-white rounded-2xl border border-[#F9E1E8] p-5 h-fit sticky top-24">
          <h3 className="font-serif text-lg text-[#4A3B40] mb-4">Resumo</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-3">
            {items.map((it, i) => (
              <div key={i} className="flex gap-2 text-sm">
                <img src={(it.product.images||[])[0] || ""} className="w-12 h-12 rounded-lg object-cover" alt="" />
                <div className="flex-1">
                  <p className="line-clamp-1 text-[#4A3B40]">{it.product.name}</p>
                  <p className="text-xs text-[#8C7A80]">{it.quantity}x {brl(it.product.promo_price || it.product.price)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pb-3 border-b border-[#F9E1E8]">
            <input value={coupon} onChange={(e)=>setCoupon(e.target.value)} placeholder="Cupom" className="input text-sm py-2 flex-1" data-testid="checkout-coupon-input" />
            <button onClick={applyCoupon} className="btn-lara-soft py-2 px-3 text-sm" data-testid="checkout-coupon-apply">Aplicar</button>
          </div>
          <div className="space-y-1.5 text-sm pt-3">
            <div className="flex justify-between"><span className="text-[#8C7A80]">Subtotal</span><span>{brl(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#7DBE9E]"><span>Cupom {couponData.code}</span><span>- {brl(discount)}</span></div>}
            {pixDisc > 0 && <div className="flex justify-between text-[#7DBE9E]"><span>5% OFF PIX</span><span>- {brl(pixDisc)}</span></div>}
            <div className="flex justify-between"><span className="text-[#8C7A80]">Frete</span><span className={finalShipping===0?'text-[#7DBE9E] font-semibold':''}>{finalShipping===0?'Grátis':brl(finalShipping)}</span></div>
            <div className="flex justify-between pt-2 mt-2 border-t border-[#F9E1E8] text-base font-bold"><span>Total</span><span className="text-[#C9708B]">{brl(total)}</span></div>
          </div>
        </aside>
      </div>
      <style>{`.input{width:100%;background:#FFF9FA;border:1px solid #F9E1E8;border-radius:12px;padding:10px 14px;outline:none;font-size:14px;color:#4A3B40}.input:focus{border-color:#C9708B}`}</style>
    </div>
  );
}
